import { useEffect, useMemo, useRef, useState } from 'react';
import type { UserAccount } from '../types';
import api from '../services/api';
import { fleetApi } from '../services/fleetApi';
import { telemetryApi } from '../services/telemetryApi';
import { userService } from '../services/userService';
import { frmsService } from '../services/frms';
import { reverseGeocodeBatch } from '../services/geocodingApi';
import type { OperationsTvHealth, OperationsTvRow, OperationsTvStatus, OperationsTvSummary } from '../components/operationsTv/types';

const REFRESH_MS = 15000;
const MAX_BACKOFF_MS = 60000;
const STATIC_DATA_TTL_MS = 5 * 60 * 1000;
const DELAYED_MINUTES = 5;
const LAST_SEEN_MINUTES = 30;
const OFFLINE_MINUTES = 120;
const AIRBORNE_KTS = 50;
const TAXI_KTS = 5;
const ROUTE_REFRESH_EVERY_CYCLES = 4;
const COORD_TEXT_RE = /^[-0-9.]+,\s[-0-9.]+$/;

type MissionLegInfo = {
  aircraftId?: string;
  origin?: string;
  destination?: string;
  etaIso?: string;
  status?: string;
  pilotId?: string;
  pilotName?: string;
  pilotEmail?: string;
  pilotRole?: string;
};

type FrmsPilot = {
  email?: string;
  photoUrl?: string;
};

type StaticBoardData = {
  aircrafts: any[];
  airstrips: Array<{ code: string; name?: string; latitude?: number; longitude?: number; country?: string }>;
  users: UserAccount[];
  userById: Map<string, UserAccount>;
  userByEmail: Map<string, UserAccount>;
  userByFullName: Map<string, UserAccount>;
  frmsByEmail: Map<string, FrmsPilot>;
};

type RouteContextData = {
  missionLegByAircraft: Map<string, MissionLegInfo>;
  tripByTail: Record<string, { pilotName?: string; destination?: string; origin?: string; lastLocation?: string } | null>;
};

const hasCoord = (lat?: number, lng?: number) =>
  typeof lat === 'number'
  && typeof lng === 'number'
  && Number.isFinite(lat)
  && Number.isFinite(lng)
  && lat >= -90
  && lat <= 90
  && lng >= -180
  && lng <= 180;

const minutesSince = (iso?: string): number | undefined => {
  if (!iso) return undefined;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return undefined;
  return Math.max(0, Math.floor((Date.now() - ts) / 60000));
};

const formatLastUpdate = (iso?: string): string => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const toCode = (code?: string) => (code || '').trim().toUpperCase();

const etaDiffMinutes = (etaIso?: string): number | undefined => {
  if (!etaIso) return undefined;
  const eta = new Date(etaIso).getTime();
  if (Number.isNaN(eta)) return undefined;
  return Math.round((eta - Date.now()) / 60000);
};

const statusFromSignals = (
  speed: number,
  freshnessMinutes: number | undefined,
  leg: MissionLegInfo | undefined,
  destination?: string
): OperationsTvStatus => {
  if (freshnessMinutes !== undefined && freshnessMinutes >= OFFLINE_MINUTES) return 'OFFLINE';
  if (freshnessMinutes !== undefined && freshnessMinutes >= LAST_SEEN_MINUTES) return 'LAST SEEN';
  if (freshnessMinutes !== undefined && freshnessMinutes >= DELAYED_MINUTES) return 'DELAYED';
  if (speed > AIRBORNE_KTS) return 'IN FLIGHT';
  if (speed > TAXI_KTS) {
    if ((leg?.status || '').toUpperCase() === 'SCHEDULED') return 'TAXI OUT';
    return destination ? 'TAXI IN' : 'TAXI OUT';
  }
  if (destination && (leg?.status || '').toUpperCase() === 'IN_FLIGHT') return 'ARRIVED';
  if (destination) return 'ON GROUND';
  if (!leg?.origin && !leg?.destination) return 'NO ACTIVE TRIP';
  return 'UNKNOWN';
};

const findNearestAirstrip = (
  lat: number | undefined,
  lng: number | undefined,
  airstrips: Array<{ code: string; name?: string; latitude?: number; longitude?: number; country?: string }>
) => {
  if (!hasCoord(lat, lng)) return null;
  let best: { code: string; name?: string; country?: string; meters: number } | null = null;
  airstrips.forEach((a) => {
    if (!hasCoord(a.latitude, a.longitude)) return;
    const dLat = ((a.latitude as number) - (lat as number)) * 111000;
    const dLng = ((a.longitude as number) - (lng as number)) * 111000 * Math.cos((lat as number) * Math.PI / 180);
    const meters = Math.sqrt(dLat * dLat + dLng * dLng);
    if (!best || meters < best.meters) {
      best = { code: a.code, name: a.name, country: a.country, meters };
    }
  });
  return best && best.meters <= 10000 ? best : null;
};

const getInitials = (name: string) => {
  const chunks = name.trim().split(/\s+/g).filter(Boolean);
  if (!chunks.length) return 'NA';
  return chunks.slice(0, 2).map((c) => c[0]?.toUpperCase() || '').join('');
};

const proxyZohoPhoto = (raw?: string) => {
  if (!raw) return undefined;
  if (!raw.startsWith('http')) return raw;
  const apiBase = import.meta.env.VITE_API_URL || '/meos/api';
  const jwt = localStorage.getItem('ma_jwt') || '';
  return `${apiBase}/auth/zoho-photo?url=${encodeURIComponent(raw)}${jwt ? `&jwt=${encodeURIComponent(jwt)}` : ''}`;
};

const buildRouteContext = (
  missions: any[],
  tripMetaList: Array<{ tail: string; meta: { pilotName?: string; destination?: string; origin?: string; lastLocation?: string } | null }>
): RouteContextData => {
  const tripByTail: RouteContextData['tripByTail'] = {};
  tripMetaList.forEach(({ tail, meta }) => {
    tripByTail[tail] = meta;
  });

  const missionLegByAircraft = new Map<string, MissionLegInfo>();
  missions.forEach((mission: any) => {
    if (!Array.isArray(mission?.Leg)) return;
    mission.Leg.forEach((leg: any) => {
      if (!leg?.aircraftId || missionLegByAircraft.has(leg.aircraftId)) return;
      const crew = Array.isArray(leg.CrewAssignment) ? leg.CrewAssignment : [];
      const pic = crew.find((c: any) => c?.role === 'PIC') || crew[0];
      const user = pic?.User;
      const pilotName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown'
        : undefined;

      missionLegByAircraft.set(leg.aircraftId, {
        aircraftId: leg.aircraftId,
        origin: leg.departureICAO || leg.from,
        destination: leg.arrivalICAO || leg.to,
        etaIso: leg.eta || leg.ETA || undefined,
        status: leg.status,
        pilotId: user?.id,
        pilotName,
        pilotEmail: user?.email || undefined,
        pilotRole: pic?.role || undefined,
      });
    });
  });

  return { missionLegByAircraft, tripByTail };
};

const deriveRows = (
  staticData: StaticBoardData,
  routeData: RouteContextData,
  snapshot: Record<string, any>,
  locationCache: Record<string, string>
): OperationsTvRow[] => {
  return staticData.aircrafts
    .filter((ac) => ac?.tailNumber !== '5H-MAA')
    .map((ac) => {
      const snap = snapshot?.[ac.tailNumber] || ac.telemetry || {};
      const leg = routeData.missionLegByAircraft.get(ac.id);
      const trip = routeData.tripByTail[ac.tailNumber];
      const speed = Number(snap.groundSpeed || 0);
      const freshness = minutesSince(snap.lastUpdate);
      const destination = toCode(leg?.destination || trip?.destination);
      const origin = toCode(leg?.origin || trip?.origin);
      const status = statusFromSignals(speed, freshness, leg, destination);
      const nearest = findNearestAirstrip(snap.lat, snap.lng, staticData.airstrips);
      const coordKey = hasCoord(snap.lat, snap.lng)
        ? `${Number(snap.lat).toFixed(4)},${Number(snap.lng).toFixed(4)}`
        : '';

      const currentLocation = trip?.lastLocation
        || (nearest ? (nearest.name || nearest.code) : undefined)
        || (coordKey && locationCache[coordKey])
        || (hasCoord(snap.lat, snap.lng) ? `${Number(snap.lat).toFixed(4)}, ${Number(snap.lng).toFixed(4)}` : 'Unknown');

      const picUser = leg?.pilotId ? staticData.userById.get(leg.pilotId) : undefined;
      const byEmail = leg?.pilotEmail ? staticData.userByEmail.get(leg.pilotEmail.toLowerCase()) : undefined;
      const byTripName = trip?.pilotName ? staticData.userByFullName.get(trip.pilotName.toLowerCase()) : undefined;
      const pilotName = leg?.pilotName || trip?.pilotName || picUser?.name || byEmail?.name || byTripName?.name || 'No Pilot Assigned';

      const frmsPilot = leg?.pilotEmail ? staticData.frmsByEmail.get(leg.pilotEmail.toLowerCase()) : undefined;
      const pilotPhotoUrl = picUser?.avatar
        || byEmail?.avatar
        || byTripName?.avatar
        || proxyZohoPhoto(frmsPilot?.photoUrl)
        || undefined;

      const etaMinutes = status === 'IN FLIGHT' ? etaDiffMinutes(leg?.etaIso) : undefined;
      const etaText = status === 'IN FLIGHT'
        ? (etaMinutes === undefined ? 'CALCULATING' : `${Math.max(0, etaMinutes)}m`)
        : (status === 'TAXI OUT' || status === 'TAXI IN')
          ? (destination ? 'Awaiting Dep' : '—')
          : '—';

      return {
        aircraftId: ac.id,
        tailNumber: ac.tailNumber,
        aircraftName: ac.name,
        aircraftImageUrl: ac.imageUrl || undefined,
        currentLocation,
        locationSubtext: nearest ? `${nearest.code}${nearest.country ? ` · ${nearest.country}` : ''}` : undefined,
        status,
        origin: origin || undefined,
        destination: destination || undefined,
        routeSubtext: destination ? `${origin || '—'} to ${destination}` : undefined,
        pilotName,
        pilotRole: leg?.pilotRole || (pilotName === 'No Pilot Assigned' ? undefined : 'PIC'),
        pilotPhotoUrl,
        lastUpdateIso: snap.lastUpdate,
        lastUpdateText: formatLastUpdate(snap.lastUpdate),
        etaText,
        etaMinutes: etaMinutes === undefined ? undefined : Math.max(0, etaMinutes),
        gpsFreshnessMinutes: freshness,
        telemetry: {
          lat: snap.lat,
          lng: snap.lng,
          altitude: snap.altitude,
          speed: snap.groundSpeed,
          heading: snap.heading,
        },
        aircraft: ac,
      } satisfies OperationsTvRow;
    })
    .sort((a, b) => a.tailNumber.localeCompare(b.tailNumber));
};

export const useOperationsTvBoard = (options?: { publicToken?: string | null; publicAlias?: string | null }) => {
  const publicToken = String(options?.publicToken || '').trim();
  const publicAlias = String(options?.publicAlias || '').trim();
  const isPublicMode = publicToken.length > 0 || publicAlias.length > 0;
  const [rows, setRows] = useState<OperationsTvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<string>('');
  const [trackerError, setTrackerError] = useState<string | null>(null);
  const [locationCache, setLocationCache] = useState<Record<string, string>>({});

  const pollTimerRef = useRef<number | null>(null);
  const locationCacheRef = useRef<Record<string, string>>({});

  useEffect(() => {
    locationCacheRef.current = locationCache;
  }, [locationCache]);

  useEffect(() => {
    if (!isPublicMode) return;
    let cancelled = false;
    let timer: number | null = null;
    let failureCount = 0;

    const scheduleNext = (delayMs: number) => {
      if (cancelled) return;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void run(false);
      }, delayMs);
    };

    const run = async (initial: boolean) => {
      try {
        const response = await api.get('/public/operations-tv', { params: publicToken ? { token: publicToken } : { alias: publicAlias } });
        if (cancelled) return;
        const data = response.data?.data || {};
        const nextRows = Array.isArray(data.rows) ? data.rows : [];
        setRows(nextRows);
        setLastRefresh(String(data.lastRefresh || new Date().toISOString()));
        setTrackerError(null);
        failureCount = 0;
        scheduleNext(REFRESH_MS);
      } catch (error: any) {
        if (cancelled) return;
        failureCount += 1;
        const backoff = Math.min(REFRESH_MS * (2 ** Math.min(failureCount, 2)), MAX_BACKOFF_MS);
        const status = String(error?.response?.status || '');
        if (status === '401') setRows([]);
        setTrackerError(
          status === '401'
            ? 'Public monitor link is invalid or expired.'
            : status === '504'
              ? 'Telemetry feed timeout (504). Displaying last known positions.'
              : 'Public monitor feed delayed. Displaying last known positions.'
        );
        scheduleNext(backoff);
      } finally {
        if (initial && !cancelled) setLoading(false);
      }
    };

    setLoading(true);
    void run(true);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isPublicMode, publicToken, publicAlias]);

  useEffect(() => {
    if (isPublicMode) return;
    let cancelled = false;
    let failureCount = 0;

    const scheduleNext = (delayMs: number) => {
      if (cancelled) return;
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = window.setTimeout(() => {
        void run(false);
      }, delayMs);
    };

    const run = async (initial: boolean) => {
      try {
        const response = await api.get('/telemetry/operations-tv');
        if (cancelled) return;

        const data = response.data?.data || {};
        const nextRows = Array.isArray(data.rows) ? data.rows : [];
        setRows(nextRows);
        setLastRefresh(String(data.lastRefresh || new Date().toISOString()));
        setTrackerError(null);
        failureCount = 0;
        scheduleNext(REFRESH_MS);
      } catch (error: any) {
        if (cancelled) return;
        failureCount += 1;
        const backoff = Math.min(REFRESH_MS * (2 ** Math.min(failureCount, 2)), MAX_BACKOFF_MS);
        const message = String(error?.response?.status || '') === '504'
          ? 'Operations board timeout (504). Displaying last known positions.'
          : 'Operations board feed delayed. Displaying last known positions.';

        setTrackerError(message);
        scheduleNext(backoff);
      } finally {
        if (initial && !cancelled) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    void run(true);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
      }
    };
  }, [isPublicMode]);

  const summary: OperationsTvSummary = useMemo(() => {
    const inFlight = rows.filter((r) => r.status === 'IN FLIGHT' || r.status === 'LIVE').length;
    const alerts = rows.filter((r) => r.status === 'DELAYED' || r.status === 'LAST SEEN' || r.status === 'OFFLINE' || r.status === 'UNKNOWN').length;
    return {
      total: rows.length,
      inFlight,
      onGround: Math.max(0, rows.length - inFlight),
      alerts,
    };
  }, [rows]);

  const health: OperationsTvHealth = useMemo(() => {
    if (trackerError) return { label: 'Feed Degraded', tone: 'danger' };
    if (!rows.length) return { label: 'No Aircraft', tone: 'warning' };
    const offlineCount = rows.filter((r) => r.status === 'OFFLINE').length;
    const delayedCount = rows.filter((r) => r.status === 'DELAYED' || r.status === 'LAST SEEN').length;
    if (offlineCount > 0) return { label: 'Tracking Delay', tone: 'warning' };
    if (delayedCount > 0) return { label: 'Partial Tracking', tone: 'warning' };
    return { label: 'All Systems Operational', tone: 'healthy' };
  }, [rows, rows.length, trackerError]);

  return {
    rows,
    loading,
    trackerError,
    summary,
    health,
    lastRefresh,
    refreshMs: REFRESH_MS,
    getInitials,
  };
};
